/* Advent Calendar 2026 campaign page
 * Loaded only by layout/theme.advent-calendar.liquid
 */

/* ---------- Advent header: mobile menu toggle ---------- */
if (!customElements.get('advent-header')) {
  customElements.define(
    'advent-header',
    class AdventHeader extends HTMLElement {
      connectedCallback() {
        this.burger = this.querySelector('.advent-header__burger');
        this.dropdown = this.querySelector('.advent-header__dropdown');
        if (!this.burger || !this.dropdown) return;

        this.burger.addEventListener('click', () => this.toggle());
        this.dropdown.addEventListener('click', (event) => {
          if (event.target.closest('a')) this.close();
        });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.close();
        });
        window.addEventListener('resize', () => {
          if (window.innerWidth >= 768) this.close();
        });
      }

      toggle() {
        this.dropdown.hidden ? this.open() : this.close();
      }

      open() {
        this.dropdown.hidden = false;
        this.burger.setAttribute('aria-expanded', 'true');
      }

      close() {
        this.dropdown.hidden = true;
        this.burger.setAttribute('aria-expanded', 'false');
      }
    }
  );
}

/* ---------- Advent doors: desktop flip + mobile select ---------- */
if (!customElements.get('advent-doors')) {
  customElements.define(
    'advent-doors',
    class AdventDoors extends HTMLElement {
      connectedCallback() {
        this.doors = Array.from(this.querySelectorAll('.advent-door'));
        this.toggleAllBtn = this.querySelector('.advent-doors__toggle-all');
        this.card = this.querySelector('[data-selected-card]');
        this.mobileMq = window.matchMedia('(max-width: 767px)');
        this.reduceMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.doors.forEach((door) => {
          door.addEventListener('click', () => this.onDoorClick(door));
          door.addEventListener('mouseenter', () => this.onDoorPeek(door));
          door.addEventListener('focusin', () => this.onDoorPeek(door));
        });
        if (this.toggleAllBtn) {
          this.toggleAllBtn.addEventListener('click', () => this.onToggleAll());
        }
        this.mobileMq.addEventListener('change', () => this.syncSelectedPanel());
      }

      onDoorClick(door) {
        // The finale door only ever flips to its greeting — no product behind it
        if (door.classList.contains('advent-door--finale')) {
          this.flipDoor(door, !door.classList.contains('is-open'));
          this.syncToggleAll();
          return;
        }
        if (this.mobileMq.matches) {
          this.selectDoor(door);
        } else {
          // Desktop: doors just flip to the image; product links live in the detail panel
          const open = !door.classList.contains('is-open');
          this.flipDoor(door, open);
          if (open) this.selectDoor(door);
          this.syncToggleAll();
          this.syncSelectedPanel();
        }
      }

      // Desktop: hovering (or tabbing to) an already-open door updates the
      // detail panel — no need to close and reopen it to see its details.
      onDoorPeek(door) {
        if (this.mobileMq.matches) return;
        if (door.classList.contains('advent-door--finale')) return;
        if (!door.classList.contains('is-open')) return;
        if (door.classList.contains('is-selected')) return;
        this.selectDoor(door);
      }

      flipDoor(door, open) {
        door.classList.toggle('is-open', open);
        door.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      onToggleAll() {
        const allOpen = this.doors.every((d) => d.classList.contains('is-open'));
        const grid = this.querySelector('.advent-doors__grid');

        // Stagger the swing into a wave; remove the class after it settles
        // so individual clicks afterwards feel instant.
        if (grid) {
          grid.classList.add('is-staggered');
          clearTimeout(this._staggerTimer);
          this._staggerTimer = setTimeout(
            () => grid.classList.remove('is-staggered'),
            this.doors.length * 30 + 700
          );
        }

        this.doors.forEach((d) => this.flipDoor(d, !allOpen));
        this.syncToggleAll();
        this.syncSelectedPanel();
      }

      // The detail panel only makes sense while a product door is open:
      // hide it when everything is closed, and fall back to another open
      // door when the selected one gets closed. Mobile always shows it.
      syncSelectedPanel() {
        if (!this.card) return;
        if (this.mobileMq.matches) {
          this.card.hidden = !this._hasSelection;
          return;
        }
        const openDoors = this.doors.filter(
          (d) => d.classList.contains('is-open') && !d.classList.contains('advent-door--finale')
        );
        if (openDoors.length === 0) {
          this.card.hidden = true;
          return;
        }
        const selected = this.doors.find((d) => d.classList.contains('is-selected'));
        if (
          !selected ||
          !selected.classList.contains('is-open') ||
          selected.classList.contains('advent-door--finale')
        ) {
          this.selectDoor(openDoors[0]);
        }
        this.card.hidden = false;
      }

      syncToggleAll() {
        if (!this.toggleAllBtn) return;
        const allOpen =
          this.doors.length > 0 && this.doors.every((d) => d.classList.contains('is-open'));
        this.toggleAllBtn.textContent = allOpen
          ? this.toggleAllBtn.dataset.closeLabel
          : this.toggleAllBtn.dataset.openLabel;
      }

      selectDoor(door) {
        this.doors.forEach((d) => d.classList.remove('is-selected'));
        door.classList.add('is-selected', 'is-seen');
        if (!this.card) return;
        this._hasSelection = true;

        // First reveal (card hidden) or reduced motion: fill instantly
        if (this.card.hidden || this.reduceMotionMq.matches) {
          this.fillCard(door.dataset);
          this.card.hidden = false;
          return;
        }

        // Soft crossfade between doors. Hover-sweep safe: while a fade is
        // running, only the latest hovered door's data is kept for the swap.
        this._pendingFill = () => this.fillCard(door.dataset);
        if (this._switching) return;
        this._switching = true;
        this.card.classList.add('is-switching');
        this._swapTimer = setTimeout(() => {
          if (this._pendingFill) this._pendingFill();
          this._pendingFill = null;
          this.card.classList.remove('is-switching');
          this._switching = false;
        }, 150);
      }

      fillCard(data) {
        const dayWrap = this.card.querySelector('[data-card-day-wrap]');
        const dayEl = this.card.querySelector('[data-card-day]');
        if (dayEl) dayEl.textContent = data.day;
        if (dayWrap) dayWrap.style.setProperty('--adv-tile-bg', data.pastel);

        this.fillCardItem(this.card.querySelector('[data-card-item]'), data, '');

        // Second product row: shown only when the door carries one
        const item2 = this.card.querySelector('[data-card-item-2]');
        if (item2) {
          const hasSecond = !!(data.product2 || data.brand2);
          item2.hidden = !hasSecond;
          if (hasSecond) {
            this.fillCardItem(item2, data, '2');
            // Single-photo mode: both products share the first product's photo
            if (data.singlePhoto === 'true') {
              const thumb2 = item2.querySelector('[data-card-thumb]');
              if (thumb2) {
                thumb2.hidden = true;
                thumb2.removeAttribute('src');
              }
            }
          }
        }
      }

      fillCardItem(item, data, suffix) {
        if (!item) return;
        const get = (key) => data[key + suffix] || '';

        item.querySelector('[data-card-brand]').textContent = get('brand');

        // The product name is the link to the product page
        const nameEl = item.querySelector('[data-card-product]');
        nameEl.textContent = get('product');
        if (get('url')) {
          nameEl.setAttribute('href', get('url'));
          nameEl.classList.add('reversed-link');
        } else {
          nameEl.removeAttribute('href');
          nameEl.classList.remove('reversed-link');
        }

        item.querySelector('[data-card-size]').textContent = get('size');

        const tagWrap = item.querySelector('[data-card-tag]');
        tagWrap.innerHTML = '';
        if (get('tag')) {
          const tag = document.createElement('span');
          tag.className = get('tagClass');
          tag.textContent = get('tag');
          tagWrap.appendChild(tag);
        }

        const thumb = item.querySelector('[data-card-thumb]');
        if (thumb) {
          thumb.hidden = !get('img');
          if (get('img')) {
            if (thumb.getAttribute('src') !== get('img')) thumb.src = get('img');
          } else {
            thumb.removeAttribute('src');
          }
        }
      }
    }
  );
}
